<template>
  <component :is="cardComponent" :label="label" :menu="menu" v-bind="$attrs">
    <slot />
    <template #controls>
      <slot v-if="lazyEditing" name="controls" />
    </template>
  </component>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type CardMenuDefinition from "@/components/controls/card/CardMenuDefinition";
import BmCollapsibleCard from "@/components/controls/card/BmCollapsibleCard.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import { useI18n } from "@/plugins/i18n";

/**
 * General purpose card that supports an explicit 'editing' state.
 *
 * When 'editing' is true it will expand a control panel to display any controls included in a named slot called 'controls'.
 * Bind :editing and listen for @change when the user toggles edit mode. If you don't want to use the editing state then simply bind 'true' into editing.
 */
interface BmEditCardProps {
  editing?: boolean;
  label?: string;
  readOnly?: boolean;
  isCollapsible?: boolean;
}

const props = withDefaults(defineProps<BmEditCardProps>(), {
  readOnly: false,
  isCollapsible: false,
});

interface BmEditCardEvents {
  (eventName: "change", editing: boolean): void;
}
const emit = defineEmits<BmEditCardEvents>();

const { $m } = useI18n();
const lazyEditing = ref(props.editing);

const toggleEditable = () => {
  lazyEditing.value = !lazyEditing.value;
  emit("change", lazyEditing.value);
};

const cardComponent = computed(() => {
  return props.isCollapsible ? BmCollapsibleCard : BmCard;
});

watch(
  () => props.editing,
  (newVal) => {
    lazyEditing.value = newVal;
  }
);

const menu = computed((): CardMenuDefinition => {
  return {
    menuItems: [
      {
        displayText: $m.edit,
        onClick: () => toggleEditable(),
        isVisible: () => !props.editing && !props.readOnly,
      },
    ],
  } as CardMenuDefinition;
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.bm-card .bm-card-title
  background-color: $bm-color-background04
</style>
